import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, getAccessToken, setAccessToken, setUnauthenticatedHandler } from '@/api/client';
import { identityApi } from '@/api/endpoints';
import type { CurrentUser } from '@/api/types';
import type { Permission } from './permissions';
import { useIdleSession } from './useIdleSession';

/**
 * The signed-in profile, retried on a server fault. A passing 5xx is not a verdict on the token,
 * and treating it as one discarded the token and signed every tab out.
 */
async function confirmProfile(): Promise<CurrentUser> {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await identityApi.me();
    } catch (cause) {
      const serverFault = cause instanceof ApiError && cause.status >= 500;
      if (!serverFault || attempt >= 3) throw cause;
      await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
    }
  }
}

function describeMinutes(minutes: number): string {
  if (minutes >= 60 && minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `${minutes} minutes`;
}

/**
 * Session state.
 *
 * The token is kept in sessionStorage (see `api/client`), so a refresh restores the session and
 * the user stays on the page they were on. On boot the status is `unknown` until the stored token
 * has been revalidated against `/me`. Password and one-time-code sign-in exchange into the same
 * application session that local development subjects use.
 */
interface AuthState {
  user: CurrentUser | null;
  status: 'unknown' | 'authenticating' | 'authenticated' | 'unauthenticated';
  error: string | null;
  signIn: (token: string) => Promise<void>;
  signInWithDevSubject: (subject: string) => Promise<void>;
  signOut: () => void;
  can: (permission: Permission | Permission[]) => boolean;
  canAny: (permissions: Permission[]) => boolean;
  /**
   * Whether this company has a part of the product at all.
   *
   * A different question from a permission: a permission says what this person may do, this says
   * what the company was sold. The server refuses an unlicensed module whatever the menu shows, so
   * this only decides what is worth offering.
   */
  hasModule: (module: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [status, setStatus] = useState<AuthState['status']>(() =>
    getAccessToken() ? 'unknown' : 'unauthenticated',
  );
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const authGeneration = useRef(0);

  const signOut = useCallback(() => {
    authGeneration.current += 1;
    // Told to the server before the token is thrown away, because the token is what authorises
    // the request. Not awaited, and a failure is ignored: the session must end on this device
    // whatever the network does, and an expired token has already achieved the same thing.
    void identityApi.endSession().catch(() => undefined);
    setAccessToken(null);
    setUser(null);
    setStatus('unauthenticated');
    // Everything cached was fetched as the previous user. Keeping it would leak their data into
    // the next session on the same device.
    queryClient.clear();
  }, [queryClient]);

  const signOutIdle = useCallback((reason?: 'idle' | 'max_lifetime') => {
    signOut();
    if (reason === 'max_lifetime') {
      let maxMins = 720;
      try {
        const stored = localStorage.getItem('dam.maxSessionMinutes');
        if (stored) maxMins = Number(stored);
      } catch {
        // storage unavailable
      }
      setError(`Your session reached its maximum duration of ${describeMinutes(maxMins)}. Sign in again to continue.`);
    } else {
      let idleMins = 10;
      try {
        const stored = localStorage.getItem('dam.idleTimeoutMinutes');
        if (stored) idleMins = Number(stored);
      } catch {
        // storage unavailable
      }
      setError(`You were signed out after ${idleMins} minutes without activity. Sign in again to continue.`);
    }
  }, [signOut]);
  useIdleSession(status === 'authenticated', signOutIdle);

  // A stored token from a previous page load is only a claim; revalidate it before trusting it.
  useEffect(() => {
    if (!getAccessToken()) return;
    let cancelled = false;
    const generation = authGeneration.current;
    confirmProfile()
      .then((profile) => {
        if (cancelled || generation !== authGeneration.current) return;
        try {
          if (!localStorage.getItem('dam.sessionStart')) {
            localStorage.setItem('dam.sessionStart', String(Date.now()));
          }
        } catch {
          // ignore
        }
        setUser(profile);
        setStatus('authenticated');
      })
      .catch(() => {
        if (cancelled || generation !== authGeneration.current) return;
        setAccessToken(null);
        setStatus('unauthenticated');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Synchronize login/logout events across tabs via localStorage StorageEvent.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea && typeof window !== 'undefined' && event.storageArea !== window.localStorage) {
        return;
      }
      if (event.key === 'dam.accessToken' || event.key === null) {
        const newToken = event.newValue;
        if (!newToken) {
          authGeneration.current += 1;
          setAccessToken(null);
          setUser(null);
          setStatus('unauthenticated');
          queryClient.clear();
        } else if (status !== 'authenticated' || (event.oldValue && event.oldValue !== newToken)) {
          authGeneration.current += 1;
          const generation = authGeneration.current;
          setAccessToken(newToken);
          setStatus('authenticating');
          confirmProfile()
            .then((profile) => {
              if (generation !== authGeneration.current) return;
              setUser(profile);
              setStatus('authenticated');
            })
            .catch(() => {
              if (generation !== authGeneration.current) return;
              setAccessToken(null);
              setUser(null);
              setStatus('unauthenticated');
            });
        }
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [status, queryClient]);

  // A 401 from any request means the session is over, wherever it happened.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      authGeneration.current += 1;
      setAccessToken(null);
      setUser(null);
      setStatus('unauthenticated');
      queryClient.clear();
    });
    return () => setUnauthenticatedHandler(null);
  }, [queryClient]);

  const signIn = useCallback(
    async (token: string) => {
      const generation = ++authGeneration.current;
      setStatus('authenticating');
      setError(null);
      setAccessToken(token);
      try {
        localStorage.setItem('dam.sessionStart', String(Date.now()));
        localStorage.setItem('dam.lastActivity', String(Date.now()));
      } catch {
        // Private-mode or blocked storage
      }
      try {
        // Confirms the token is valid *and* that this identity is provisioned as a DAM user.
        // A token that authenticates but resolves to no user is not a usable session.
        const profile = await confirmProfile();
        if (generation !== authGeneration.current) return;
        setUser(profile);
        setStatus('authenticated');
      } catch (cause) {
        if (generation !== authGeneration.current) throw cause;
        setAccessToken(null);
        setStatus('unauthenticated');
        setError(
          cause instanceof ApiError
            ? cause.status === 401
              ? 'That token was rejected, or the account is not provisioned in this system.'
              : cause.message
            : 'Sign-in failed.',
        );
        throw cause;
      }
    },
    [],
  );

  const signInWithDevSubject = useCallback(
    async (subject: string) => {
      setStatus('authenticating');
      setError(null);
      let accessToken: string;
      try {
        ({ access_token: accessToken } = await identityApi.localToken(subject));
      } catch (cause) {
        setStatus('unauthenticated');
        setError(
          cause instanceof ApiError
            ? cause.status === 404 || cause.status === 403
              ? 'The development token issuer is not enabled on this server.'
              : cause.message
            : 'Sign-in failed.',
        );
        throw cause;
      }
      // Refusals after the token is issued (an unprovisioned account, another company's address)
      // keep the reason signIn gives them.
      await signIn(accessToken);
    },
    [signIn],
  );

  const permissionSet = useMemo(() => new Set(user?.permissions ?? []), [user]);
  // Null when the server said nothing about modules - an older server, or a company nobody sold
  // anything to. That must mean "everything", not "nothing": defaulting the other way would empty
  // the menu of a working installation the moment this shipped.
  const moduleSet = useMemo(() => (user?.modules ? new Set(user.modules) : null), [user]);

  const can = useCallback(
    (permission: Permission | Permission[]) =>
      Array.isArray(permission)
        ? permission.every((entry) => permissionSet.has(entry))
        : permissionSet.has(permission),
    [permissionSet],
  );

  const canAny = useCallback(
    (permissions: Permission[]) => permissions.some((entry) => permissionSet.has(entry)),
    [permissionSet],
  );

  const hasModule = useCallback(
    (module: string) => moduleSet === null || moduleSet.has(module),
    [moduleSet],
  );

  const value = useMemo<AuthState>(
    () => ({ user, status, error, signIn, signInWithDevSubject, signOut, can, canAny, hasModule }),
    [user, status, error, signIn, signInWithDevSubject, signOut, can, canAny, hasModule],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}

/** Convenience for the common case of gating a single action. */
export function usePermission(permission: Permission): boolean {
  return useAuth().can(permission);
}
